import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Button, Card, Col, Row, Stack,
} from 'react-bootstrap';
import { BsPlayFill, BsFilePdf } from 'react-icons/bs';

import useUser from 'hooks/useUser';
import { ContentType, getContentType } from './utils';

interface Content {
  id: string;
  title: string;
  description?: string;
  category?: string;
  s3FileUrl?: string;
  s3FileThumbnailDownloadUrl?: string;
}

interface FileContentListProps {
  title: string;
  contentList: Content[];
  previewIcon?: React.ReactNode;
}

const FileContentList: React.FC<FileContentListProps> = ({
  title,
  contentList,
  previewIcon,
}) => {
  const { canUpdateTrainingContent } = useUser();
  const navigate = useNavigate();

  if (!contentList.length) return null;

  return (
    <div className="mb-4">
      <h4>{title}</h4>
      <Row xs={1} sm={2} md={3} xl={4} xxl={5} className="g-4">
        {contentList.map((content) => (
          <Col key={content.id}>
            <Card
              className="overflow-hidden text-decoration-none"
              as={Link}
              to={`view/${content.id}`}
            >
              <div
                className="bg-primary position-relative"
                style={{
                  paddingTop: '60%',
                  backgroundImage: `url("${content.s3FileThumbnailDownloadUrl || '/assets/No-Preview-Available.jpg'}")`,
                  backgroundRepeat: 'no-repeat',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }}
              >
                <div className="position-absolute top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center text-white bg-dark bg-opacity-50">
                  {previewIcon}
                </div>
                {canUpdateTrainingContent && (
                  <div className="position-absolute top-0 end-0">
                    <Button
                      variant="secondary"
                      onClick={(e) => {
                        e.preventDefault();
                        navigate(content.id);
                      }}
                    >
                      Edit
                    </Button>
                  </div>
                )}
              </div>
              <Card.Body className="border-top py-0 px-2">
                <Card.Title className="text-decoration-underline">
                  {content.title}
                </Card.Title>
                <Card.Subtitle className="mb-2 text-muted">
                  {content.description}
                </Card.Subtitle>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>
    </div>
  );
};

interface TrainingTabProps {
  catId?: string;
  contents?: Content[];
}

const TrainingTab: React.FC<TrainingTabProps> = ({
  catId = 'All',
  contents = [],
}) => {
  const { canUpdateTrainingContent } = useUser();
  const navigate = useNavigate();

  const tabData = React.useMemo(
    () => (catId === 'All'
      ? contents
      : contents.filter(({ category }) => category === catId)),
    [catId, contents],
  );

  const contentData = React.useMemo(() => {
    type ContentTypeKey = typeof ContentType[keyof typeof ContentType];

    return tabData.reduce<Record<ContentTypeKey, Content[]>>(
      (res, content) => {
        const fileType = getContentType(content.s3FileUrl);
        return {
          ...res,
          [fileType]: [...res[fileType], content],
        };
      },
      {
        [ContentType.VIDEO]: [],
        [ContentType.PDF]: [],
        [ContentType.TEXT]: [],
      },
    );
  }, [tabData]);

  return (
    <div className="border border-top-0 p-3">
      <FileContentList
        title="Video"
        contentList={contentData[ContentType.VIDEO]}
        previewIcon={<BsPlayFill className="h-50 w-50" />}
      />
      <FileContentList
        title="PDF"
        contentList={contentData[ContentType.PDF]}
        previewIcon={<BsFilePdf className="h-50 w-50" />}
      />
      <Stack gap={4} className="pt-4">
        {contentData[ContentType.TEXT].map((content) => (
          <Card
            key={content.id}
            as={Link}
            to={`view/${content.id}`}
            className="text-decoration-none"
          >
            <Card.Body>
              <Card.Title className="text-decoration-underline">
                {content.title}
              </Card.Title>
              <Card.Text>{content.description}</Card.Text>
              {canUpdateTrainingContent && (
                <Button
                  variant="secondary"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(content.id);
                  }}
                >
                  Edit
                </Button>
              )}
            </Card.Body>
          </Card>
        ))}
      </Stack>
    </div>
  );
};

export default TrainingTab;
